import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Modal from "@material-ui/core/Modal";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import CloseIcon from "@material-ui/icons/Close";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

const useStyles = makeStyles((theme) => ({
  scrollContainer: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: theme.spacing(3),
    padding: theme.spacing(2),
  },
  helpCard: {
    position: "relative",
    width: "100%",
    borderRadius: theme.spacing(1),
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: theme.shadows[2],
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: theme.shadows[8],
    },
  },
  thumbnail: {
    width: "100%",
    aspectRatio: "16/9",
    objectFit: "cover",
    display: "block",
  },
  cardBody: {
    padding: theme.spacing(1.5),
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  cardDescription: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  videoModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  videoModalContent: {
    outline: "none",
    width: "90%",
    maxWidth: 1024,
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  videoAspect: {
    position: "relative",
    paddingTop: "56.25%",
  },
  videoIframe: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    zIndex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.75)",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 2),
    color: theme.palette.text.disabled,
    gap: theme.spacing(1),
  },
  emptyIcon: {
    fontSize: 56,
    opacity: 0.35,
  },
}));

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (videoId) => setSelectedVideo(videoId);
  const closeVideoModal = () => setSelectedVideo(null);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Escape") closeVideoModal();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("helps.title")} ({records.length})</Title>
      </MainHeader>

      <div className={classes.scrollContainer}>
        {records.length === 0 ? (
          <Box className={classes.emptyState}>
            <HelpOutlineIcon className={classes.emptyIcon} />
            <Typography variant="body2">
              Nenhum conteúdo de ajuda disponível no momento.
            </Typography>
          </Box>
        ) : (
          <div className={classes.grid}>
            {records.map((record, key) => (
              <Paper
                key={key}
                className={classes.helpCard}
                onClick={() => openVideoModal(record.video)}
                elevation={2}
              >
                <img
                  src={`https://img.youtube.com/vi/${record.video}/mqdefault.jpg`}
                  alt={record.title}
                  className={classes.thumbnail}
                />
                <div className={classes.cardBody}>
                  <Typography className={classes.cardTitle}>
                    {record.title}
                  </Typography>
                  {record.description && (
                    <Typography className={classes.cardDescription}>
                      {record.description}
                    </Typography>
                  )}
                </div>
              </Paper>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        className={classes.videoModal}
      >
        <div className={classes.videoModalContent}>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={closeVideoModal}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {selectedVideo && (
            <div className={classes.videoAspect}>
              <iframe
                className={classes.videoIframe}
                src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
      </Modal>
    </MainContainer>
  );
};

export default Helps;
